import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import InvoiceHireForm from '../../components/Form/InvoiceHireForm';
import InvoiceHireLetterGenerator from '../../components/HR/InvoiceHireLetterGenerator';
import Button from '../../components/Form/Button';
import { InvoiceHire } from '../../types';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import { useInvoiceHires, useCreateInvoiceHire, useUpdateInvoiceHire, useDeleteInvoiceHire } from '../../hooks/useInvoiceHires';
import { useClients } from '../../hooks/useClients';
import { useDeductions } from '../../hooks/useDeductions';
import { saveInvoiceHireItems, deleteRemovedItems, fetchInvoiceHireItems } from './invoiceHireItemsSync';
import { useToast } from '../../contexts/ToastContext';
import { resolveCurrencyCode } from '../../utils/currencyUtils';
import { bumpCodePrefixSequence } from '../../utils/codePrefixUtils';
import { PageHeader, FilterBar, StatsCards, ErrorState, TabBar } from '../../components/Common';
import type { FilterConfig, StatCard } from '../../components/Common';
import { buildClientIdSet, isNoteType } from '../Invoices/clientHistoryHelpers';
import { matchesHireFilters, buildStatusStats, buildTypeStats, typeLabels, buildDeductionTotals, deductionTotalFor, buildHireExportRows } from './invoiceHireListHelpers';

const InvoiceHirePage: React.FC = () => {
  const { showToast } = useToast();
  
  const queryOptions = useMemo(() => ({
    sort: [{ field: 'createdAt', order: 'desc' as const }],
    pagination: { pageSize: 1000 },
    // Relations (customer, vessel, foa, currency) must be populated so the
    // edit form pre-fills every saved field
    populate: '*'
  }), []);
  
  const { data: invoiceHires = [], isLoading, error, refetch } = useInvoiceHires(queryOptions);
  const { data: clients = [] } = useClients(queryOptions);
  const { data: deductions = [] } = useDeductions(queryOptions);
  const deductionTotals = useMemo(() => buildDeductionTotals(deductions), [deductions]);
  
  const createMutation = useCreateInvoiceHire(() => {
    showToast('Invoice hire created successfully', 'success');
    refetch();
    setShowForm(false);
    setEditingInvoiceHire(undefined);
  });
  
  const updateMutation = useUpdateInvoiceHire(() => {
    showToast('Invoice hire updated successfully', 'success');
    refetch();
    setShowForm(false);
    setEditingInvoiceHire(undefined);
  });
  
  const deleteMutation = useDeleteInvoiceHire(() => {
    showToast('Invoice hire deleted successfully', 'success');
    refetch();
  });
  
  // "?new=1" (e.g. from Client History's Add New) opens the create form directly
  const [searchParams, setSearchParams] = useSearchParams();
  const [showForm, setShowForm] = useState(searchParams.get('new') === '1');
  const [editingInvoiceHire, setEditingInvoiceHire] = useState<InvoiceHire | undefined>();

  useEffect(() => {
    if (searchParams.get('new') === '1') {
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const [showLetterGenerator, setShowLetterGenerator] = useState(false);
  const [selectedInvoiceHireForLetter, setSelectedInvoiceHireForLetter] = useState<InvoiceHire | undefined>();
  const [activeTab, setActiveTab] = useState<'invoices' | 'debit_notes'>('invoices');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [clientFilter, setClientFilter] = useState<string>('all');
  const [dateFromFilter, setDateFromFilter] = useState<string>('');
  const [dateToFilter, setDateToFilter] = useState<string>('');
  const [amountFromFilter, setAmountFromFilter] = useState<string>('');
  const [amountToFilter, setAmountToFilter] = useState<string>('');
  const [currencyFilter, setCurrencyFilter] = useState<string>('all');

  const getClientName = useCallback((clientId: string) => {
    const client = clients.find((c: any) => c.documentId === clientId || String(c.id) === String(clientId));
    return client ? client.name : 'Unknown Client';
  }, [clients]);

  // Invoices may reference the client by documentId or numeric id — accept both
  const clientIds = useMemo(() => buildClientIdSet(clients, clientFilter), [clients, clientFilter]);

  // All rows matching the filters — feeds the totals cards so they react to filters
  const filteredAll = useMemo(() => {
    const f = { searchTerm, statusFilter, typeFilter, clientIds, currencyFilter,
      dateFromFilter, dateToFilter, amountFromFilter, amountToFilter };
    return invoiceHires.filter(invoice => matchesHireFilters(invoice, f, getClientName));
  }, [invoiceHires, searchTerm, statusFilter, typeFilter, clientIds, dateFromFilter, dateToFilter, amountFromFilter, amountToFilter, currencyFilter, getClientName]);

  // Paid invoices are moved to Client History and hidden from this list
  const filteredInvoiceHires = useMemo(
    () => filteredAll.filter(invoice => invoice.status !== 'paid'),
    [filteredAll]
  );

  // Debit/credit notes get their own tab, like Client History
  const invoiceRows = useMemo(() => filteredInvoiceHires.filter(i => !isNoteType(i.type)), [filteredInvoiceHires]);
  const noteRows = useMemo(() => filteredInvoiceHires.filter(i => isNoteType(i.type)), [filteredInvoiceHires]);
  const tabRows = activeTab === 'invoices' ? invoiceRows : noteRows;

  const handleEdit = async (invoiceHire: InvoiceHire) => {
    let items = invoiceHire.items;
    if ((!items || items.length === 0) && invoiceHire.documentId) {
      items = await fetchInvoiceHireItems(invoiceHire.documentId);
    }
    setEditingInvoiceHire({ ...invoiceHire, items: items || [] });
    setShowForm(true);
  };
  const handleDelete = async (invoiceHire: InvoiceHire) => {
    if (window.confirm(`Are you sure you want to delete invoice ${invoiceHire.invoiceNumber}?`)) {
      try { await deleteMutation.execute(invoiceHire); } catch (error) { console.error('Delete invoice hire error:', error); }
    }
  };
  const handleAdd = () => { setEditingInvoiceHire(undefined); setShowForm(true); };

  const handleFormSubmit = async (data: Partial<InvoiceHire>) => {
    try {
      // Items live in their own collection; save them first and link by documentId
      const fallbackDate = data.invoiceDate || new Date().toISOString().split('T')[0];
      const itemIds = await saveInvoiceHireItems(data.items || [], fallbackDate);
      if (editingInvoiceHire) {
        await deleteRemovedItems(editingInvoiceHire.items, itemIds);
        const updatedData: any = { ...editingInvoiceHire, ...data, invoiceHireItems: itemIds };
        await updateMutation.execute({ id: editingInvoiceHire.id, data: updatedData });
      } else {
        await createMutation.execute({ ...data, invoiceHireItems: itemIds } as any);
        // Persist the used sequence so the next invoice number advances
        await bumpCodePrefixSequence(data.invoiceNumber);
      }
    } catch (error) { console.error('Save invoice hire error:', error); }
  };

  const handleFormCancel = () => { setShowForm(false); setEditingInvoiceHire(undefined); };
  const handleGenerateLetter = async (invoiceHire: InvoiceHire) => {
    // Items live in their own collection; load them so the print shows them
    let items = invoiceHire.items;
    if ((!items || items.length === 0) && invoiceHire.documentId) {
      items = await fetchInvoiceHireItems(invoiceHire.documentId);
    }
    setSelectedInvoiceHireForLetter({ ...invoiceHire, items: items || [] });
    setShowLetterGenerator(true);
  };
  const handleLetterGenerate = (data: any) => { console.log('Generated invoice letter:', data); setShowLetterGenerator(false); setSelectedInvoiceHireForLetter(undefined); };
  const handleLetterCancel = () => { setShowLetterGenerator(false); setSelectedInvoiceHireForLetter(undefined); };

  const handleStatusUpdate = async (invoiceHire: InvoiceHire, newStatus: InvoiceHire['status']) => {
    try { await updateMutation.execute({ id: invoiceHire.id, data: { ...invoiceHire, status: newStatus } }); }
    catch (error) { console.error('Update invoice hire status error:', error); }
  };

  const buildExportData = () => buildHireExportRows(tabRows, getClientName, deductionTotals);
  const handleExport = () => exportToExcel(buildExportData(), `invoice_hire_${new Date().toISOString().split('T')[0]}`);
  const handlePrint = () => printAsExcel(buildExportData(), 'Invoice Hire');

  const clearFilters = () => {
    setStatusFilter('all'); setTypeFilter('all'); setClientFilter('all'); setCurrencyFilter('all');
    setDateFromFilter(''); setDateToFilter(''); setAmountFromFilter(''); setAmountToFilter(''); setSearchTerm('');
  };

  // Cards follow the active filters (incl. currency) and show real currency codes
  const statusStats: StatCard[] = buildStatusStats(filteredAll);
  const typeStats: StatCard[] = buildTypeStats(filteredAll);

  const filters: FilterConfig[] = [
    { key: 'status', label: 'Status', type: 'select', value: statusFilter, onChange: setStatusFilter,
      options: [{ value: 'all', label: 'All Statuses' }, { value: 'draft', label: 'Draft' }, { value: 'sent', label: 'Sent' }, { value: 'paid', label: 'Paid' }, { value: 'overdue', label: 'Overdue' }, { value: 'cancelled', label: 'Cancelled' }] },
    { key: 'type', label: 'Type', type: 'select', value: typeFilter, onChange: setTypeFilter,
      options: [{ value: 'all', label: 'All Types' }, { value: 'invoice_hire', label: 'Invoice Hire' }, { value: 'invoice_accommodation', label: 'Invoice Accommodation' }, { value: 'debit_note', label: 'Debit' }, { value: 'credit_note', label: 'Credit' }] },
    { key: 'client', label: 'Client', type: 'select', value: clientFilter, onChange: setClientFilter,
      options: [{ value: 'all', label: 'All Clients' }, ...clients.map((c: any) => ({ value: c.documentId || String(c.id), label: c.name }))] },
    { key: 'currency', label: 'Currency', type: 'select', value: currencyFilter, onChange: setCurrencyFilter,
      options: [{ value: 'all', label: 'All Currencies' }, { value: 'USD', label: 'USD' }, { value: 'EGP', label: 'EGP' }] },
    { key: 'dateFrom', label: 'From Date', type: 'date', value: dateFromFilter, onChange: setDateFromFilter },
    { key: 'dateTo', label: 'To Date', type: 'date', value: dateToFilter, onChange: setDateToFilter },
    { key: 'amountFrom', label: 'Min Amount', type: 'number', value: amountFromFilter, onChange: setAmountFromFilter, placeholder: '0.00', step: '0.01' },
    { key: 'amountTo', label: 'Max Amount', type: 'number', value: amountToFilter, onChange: setAmountToFilter, placeholder: '999999.99', step: '0.01' },
  ];

  if (isLoading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ocean-600"></div>
    </div>
  );

  if (error) return <ErrorState entityName="invoice hires" onRetry={() => refetch()} />;

  if (showForm) return (
    <div className="space-y-6">
      <InvoiceHireForm invoiceHire={editingInvoiceHire} onSubmit={handleFormSubmit} onCancel={handleFormCancel} />
    </div>
  );

  if (showLetterGenerator && selectedInvoiceHireForLetter) {
    return <InvoiceHireLetterGenerator invoiceHire={selectedInvoiceHireForLetter} onClose={handleLetterCancel} onGenerate={handleLetterGenerate} />;
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Invoices" description="Manage all invoice types (hire, accommodation, debit notes, credit notes)" />
      <StatsCards stats={statusStats} columns={5} />
      <StatsCards stats={typeStats} columns={4} />

      <TabBar
        tabs={[{ key: 'invoices', label: `Invoices (${invoiceRows.length})` },
          { key: 'debit_notes', label: `Debit / Credit Notes (${noteRows.length})` }]}
        active={activeTab} onChange={(key) => setActiveTab(key as 'invoices' | 'debit_notes')}
      />

      <FilterBar
        searchTerm={searchTerm} onSearchChange={setSearchTerm} searchPlaceholder="Search invoices..."
        filters={filters} onClearFilters={clearFilters}
        totalCount={invoiceHires.length} filteredCount={tabRows.length}
        entityName={activeTab === 'invoices' ? 'Invoices' : 'Debit/Credit Notes'}
        onAdd={handleAdd} addLabel="Add New Invoice" onExport={handleExport} onPrint={handlePrint}
      />

      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice #</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Project</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Original Total</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deductions</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remaining (Net)</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {tabRows.map((invoiceHire) => (
                <tr key={invoiceHire.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{invoiceHire.invoiceNumber}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      invoiceHire.type === 'invoice_hire' ? 'bg-purple-100 text-purple-800' :
                      invoiceHire.type === 'invoice_accommodation' ? 'bg-orange-100 text-orange-800' :
                      invoiceHire.type === 'debit_note' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                    }`}>{typeLabels[invoiceHire.type] || 'Credit Note'}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{getClientName(invoiceHire.customerId)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{invoiceHire.projectId || 'N/A'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{new Date(invoiceHire.invoiceDate).toLocaleDateString()}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {invoiceHire.originalTotal != null
                      ? `${Number(invoiceHire.originalTotal).toFixed(2)} ${resolveCurrencyCode(invoiceHire.currency)}`
                      : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">
                    {deductionTotalFor(deductionTotals, invoiceHire) > 0
                      ? `- ${deductionTotalFor(deductionTotals, invoiceHire).toLocaleString()} ${resolveCurrencyCode(invoiceHire.currency)}`
                      : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{invoiceHire.netTotal.toFixed(2)} {resolveCurrencyCode(invoiceHire.currency)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      invoiceHire.status === 'paid' ? 'bg-green-100 text-green-800' :
                      invoiceHire.status === 'sent' ? 'bg-blue-100 text-blue-800' :
                      invoiceHire.status === 'overdue' ? 'bg-red-100 text-red-800' :
                      invoiceHire.status === 'cancelled' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'
                    }`}>{invoiceHire.status.charAt(0).toUpperCase() + invoiceHire.status.slice(1)}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-y-2">
                    <div className="flex space-x-2">
                      <Button onClick={() => handleEdit(invoiceHire)} variant="secondary" size="sm">Edit</Button>
                      <Button onClick={() => handleDelete(invoiceHire)} variant="danger" size="sm">Delete</Button>
                    </div>
                    <div className="flex space-x-2">
                      <Button onClick={() => handleGenerateLetter(invoiceHire)} variant="primary" size="sm" className="w-full">Generate Invoice</Button>
                    </div>
                    {invoiceHire.status === 'draft' && (
                      <Button onClick={() => handleStatusUpdate(invoiceHire, 'sent')} variant="primary" size="sm" className="w-full text-xs">Mark Sent</Button>
                    )}
                    {invoiceHire.status === 'sent' && (
                      <Button onClick={() => handleStatusUpdate(invoiceHire, 'paid')} variant="primary" size="sm" className="w-full text-xs">Mark Paid</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {tabRows.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">{activeTab === 'invoices' ? 'No invoices found' : 'No debit or credit notes found'}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvoiceHirePage;
